import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";
import { crearFacturasMasivas } from "../../asientos/hooks/useAsientos";
import SendEmailModal from "../components/SendEmailModal";
import { MonedaSelector } from "../../components/MonedaSelector";
import { useTipoCambio } from "../../services/useTipoCambio";
import styles from "../styles/addInvoice.module.css";

const fmt = (n) => Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2 });

const MESES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

function conceptoAuto(nombre, fechaEmision) {
  if (!nombre) return "";
  let month, year;
  if (fechaEmision) {
    [year, month] = fechaEmision.split("-").map(Number);
    month -= 1;
  } else {
    const d = new Date(); month = d.getMonth(); year = d.getFullYear();
  }
  return `${nombre} — ${MESES[month]} ${year}`;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

export default function AddFacturaMasiva() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const today    = new Date().toISOString().split("T")[0];
  const { tipoCambio, loading: loadingTC } = useTipoCambio();

  const [form, setForm] = useState({
    fecha_emision:         today,
    fecha_vencimiento:     addDays(today, 5),
    concepto:              "",
    monto:                 "",
    cuenta_ingreso:        null,
    cuenta_ingreso_nombre: "",
    moneda:                "CRC",
    tipo_cambio:           1,
  });

  const [cuentasIngreso, setCuentasIngreso] = useState([]);
  const [filiales,       setFiliales]       = useState([]);
  const [filialIds,      setFilialIds]      = useState(new Set());
  const [search,         setSearch]         = useState("");
  const [errors,         setErrors]         = useState({});
  const [saving,         setSaving]         = useState(false);
  const [apiErr,         setApiErr]         = useState("");
  const [resultado,      setResultado]      = useState(null);
  const [emailModal,     setEmailModal]     = useState(null);

  useEffect(() => {
    apiFetch("/api/residents/filial/?page_size=9999")
      .then((r) => r.json())
      .then((d) => setFiliales(d.results ?? d ?? []));

    apiFetch("/api/finance/catalogo/detalle/?tipo=INGRESO")
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : (d.results ?? []);
        setCuentasIngreso(list);
        if (list.length > 0) {
          const primera = list[0];
          setForm((p) => ({
            ...p,
            cuenta_ingreso:        primera.id,
            cuenta_ingreso_nombre: primera.nombre,
            concepto:              conceptoAuto(primera.nombre, p.fecha_emision),
          }));
        }
      });
  }, []);

  function set(f, v) { setForm((p) => ({ ...p, [f]: v })); }

  function handleFechaEmision(fecha) {
    setForm((p) => ({
      ...p,
      fecha_emision:     fecha,
      fecha_vencimiento: fecha ? addDays(fecha, 5) : "",
      concepto:          conceptoAuto(p.cuenta_ingreso_nombre, fecha),
    }));
  }

  function handleCuentaIngreso(e) {
    const id  = Number(e.target.value);
    const obj = cuentasIngreso.find((c) => c.id === id);
    setForm((p) => ({
      ...p,
      cuenta_ingreso:        id || null,
      cuenta_ingreso_nombre: obj?.nombre ?? "",
      concepto:              conceptoAuto(obj?.nombre ?? "", p.fecha_emision),
    }));
  }

  function toggleFilial(id) {
    setFilialIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleTodas() {
    const vis     = filialesFiltradas;
    const todosOn = vis.every((f) => filialIds.has(f.id));
    setFilialIds((prev) => {
      const next = new Set(prev);
      if (todosOn) vis.forEach((f) => next.delete(f.id));
      else         vis.forEach((f) => next.add(f.id));
      return next;
    });
  }

  const filialesFiltradas = filiales.filter((f) =>
    !search || f.idFilial.toLowerCase().includes(search.toLowerCase())
  );

  function validate() {
    const e = {};
    if (!form.fecha_emision)                    e.fecha_emision    = t("masiva.errRequerida");
    if (!form.fecha_vencimiento)                e.fecha_vencimiento = t("masiva.errRequerida");
    if (!form.concepto.trim())                  e.concepto         = t("masiva.errRequerido");
    if (!form.monto || Number(form.monto) <= 0) e.monto            = t("masiva.errMontoMayor");
    if (!form.cuenta_ingreso)                   e.cuenta_ingreso   = t("masiva.errCuenta");
    if (filialIds.size === 0)                   e.filiales         = t("masiva.errFiliales");
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleGuardar() {
    if (!validate()) return;
    setSaving(true);
    setApiErr("");
    try {
      const res = await crearFacturasMasivas({
        fecha_emision:     form.fecha_emision,
        fecha_vencimiento: form.fecha_vencimiento,
        concepto:          form.concepto.trim(),
        monto:             Number(form.monto),
        cuenta_ingreso:    form.cuenta_ingreso,
        filiales:          Array.from(filialIds),
        moneda:            form.moneda || "CRC",
        tipo_cambio:       Number(form.tipo_cambio) || 1,
      });
      setResultado(res);
    } catch (e) {
      setApiErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const errorCount = Object.keys(errors).length;
  const isUSD      = form.moneda === "USD";
  const tc         = Number(form.tipo_cambio) || 1;

  // ── Breadcrumb ────────────────────────────────────────────────────────────
  const breadcrumb = (
    <span className={styles.inactive}>
      <Link to="/">
        <span className={styles.inactive}>{t("common.inicio")} /</span>
      </Link>
      <Link to="/finanzas/cxc">
        <span className={styles.inactive}> {t("cxc.titulo")} /</span>
      </Link>
      <Link to="/finanzas/facturas">
        <span className={styles.inactive}> {t("sidebar.facturas")} / </span>
      </Link>
      <span className={styles.active}>{t("masiva.breadcrumbMasiva")}</span>
    </span>
  );

  // ── Pantalla de resultado ─────────────────────────────────────────────────
  if (resultado) {
    return (
      <>
        {breadcrumb}
        <h2 className="mt-2 mb-2 p-0 title">{t("masiva.titulo")}</h2>

        <div className="d-flex align-items-center justify-content-center" style={{ margin: "24px 0" }}>
          <div style={{
            background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 8, padding: 32, textAlign: "center", maxWidth: 480, width: "100%",
          }}>
            <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>
              {resultado.errores?.length === 0 ? "✅" : "⚠️"}
            </div>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)", margin: "0 0 6px" }}>
              {t("masiva.creadasMsg", { n: resultado.creadas })}
            </h2>

            {resultado.errores?.length > 0 && (
              <p style={{ color: "#fbbf24", fontSize: 14 }}>
                {t("masiva.filialConError", { n: resultado.errores.length })}
              </p>
            )}

            {resultado.asiento && (
              <div style={{
                margin: "16px auto 0", maxWidth: 380,
                background: "rgba(74,222,128,0.07)",
                border: "1px solid rgba(74,222,128,0.25)",
                borderRadius: 6, padding: "12px 16px", textAlign: "left",
              }}>
                <div style={{ color: "#4ade80", fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                  <i className="fa-solid fa-book-open" style={{ marginRight: 6 }} />
                  {t("masiva.asientoGenerado")}
                </div>
                <div style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                  {t("masiva.asientoNumero")}{" "}
                  <strong style={{ color: "var(--text-primary)", fontFamily: "Inter" }}>
                    {resultado.asiento.numero}
                  </strong>
                </div>
                <div style={{ color: "var(--text-secondary)", fontSize: 13, marginTop: 2 }}>
                  {t("masiva.asientoTotal")}{" "}
                  <strong className="monto-typo" style={{ color: "var(--text-primary)" }}>
                    ₡{fmt(resultado.asiento.total_debitos)}
                  </strong>
                  &nbsp;·&nbsp;{resultado.asiento.n_lineas} {t("masiva.asientoLineas")}
                </div>
              </div>
            )}

            {resultado.errores?.length > 0 && (
              <div style={{
                margin: "12px auto 0", maxWidth: 380,
                background: "rgba(248,113,113,0.07)",
                border: "1px solid rgba(248,113,113,0.25)",
                borderRadius: 6, padding: "10px 14px", textAlign: "left",
              }}>
                <p style={{ color: "#f87171", fontWeight: 600, fontSize: 13, margin: "0 0 4px" }}>
                  {t("masiva.erroresLabel")}
                </p>
                {resultado.errores.map((e, i) => (
                  <div key={i} style={{ color: "var(--text-secondary)", fontSize: 12, marginBottom: 2 }}>
                    {t("masiva.filialErrorItem", { id: e.filial_id, error: e.error })}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 24, flexWrap: "wrap" }}>
              <button
                onClick={() => { setResultado(null); setFilialIds(new Set()); }}
                className={`${styles.btn} ${styles["btn-clean-form"]}`}
              >
                {t("masiva.crearMas")}
              </button>

              {resultado.facturas?.length > 0 && (
                <button
                  className={`${styles.btn} ${styles["btn-save"]}`}
                  onClick={() => setEmailModal(
                    resultado.facturas.map((f) => ({
                      id_factura:     f.id,
                      numero_factura: f.numero_factura,
                      monto:          f.monto,
                      filial:         { idFilial: f.filial },
                    }))
                  )}
                >
                  <i className="fa-solid fa-envelope" />
                  {t("masiva.enviarCorreosBtn", { n: resultado.facturas.length })}
                </button>
              )}

              <button
                onClick={() => navigate("/finanzas/facturas")}
                className={`${styles.btn} ${styles["btn-clean-form"]}`}
              >
                {t("masiva.verFacturas")}
              </button>
            </div>
          </div>
        </div>

        {emailModal && (
          <SendEmailModal
            facturas={emailModal}
            onClose={() => setEmailModal(null)}
            onSuccess={() => setEmailModal(null)}
          />
        )}
      </>
    );
  }

  // ── Formulario principal ──────────────────────────────────────────────────
  return (
    <>
      {breadcrumb}

      <h2 className="mt-2 mb-2 p-0 title">{t("masiva.titulo")}</h2>

      {/* ── Resumen de errores de validación ──────────────────────── */}
      {errorCount > 0 && (
        <div style={{
          background: "rgba(239,68,68,0.08)",
          border: "1px solid rgba(239,68,68,0.35)",
          borderLeft: "4px solid #ef4444",
          borderRadius: 8, padding: "12px 18px", marginBottom: 16,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8,
            color: "#f87171", fontWeight: 600, marginBottom: 6 }}>
            <i className="fa-solid fa-triangle-exclamation" />
            Hay {errorCount} campo{errorCount > 1 ? "s" : ""} con error
          </div>
          <ul style={{ margin: 0, paddingLeft: 20, color: "#fca5a5", fontSize: "0.83rem" }}>
            {Object.values(errors).map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Error de API ──────────────────────────────────────────── */}
      {apiErr && (
        <div style={{
          background: "rgba(239,68,68,0.08)",
          border: "1px solid rgba(239,68,68,0.35)",
          borderLeft: "4px solid #ef4444",
          borderRadius: 8, padding: "12px 18px", marginBottom: 16,
          color: "#f87171", fontSize: "0.88rem",
          display: "flex", alignItems: "center", gap: 8,
        }}>
          <i className="fa-solid fa-circle-exclamation" />
          {apiErr}
        </div>
      )}

      {/* ── Contenedor principal (misma estructura que AddInvoice) ── */}
      <div className={styles["invoice-container"]}>

        {/* ── Columna izquierda: Datos de la factura ─────────────── */}
        <div className={styles["invoice-details"]}>
          <div className={styles["invoice-details-header"]}>
            <h1>
              <span className={styles["icon-invoice-details"]}>
                <i className="fa-solid fa-layer-group" />
              </span>
              {t("masiva.datosFactura")}
            </h1>
          </div>

          <div className={styles["invoice-details-body"]} style={{ marginTop: 16 }}>
            {/* Fechas */}
            <div className="row">
              <div className="col">
                <label>{t("masiva.fechaEmision")} <span style={{ color: "#f87171" }}>*</span></label>
                <input type="date" className="form-control"
                  style={{ colorScheme: "light dark", borderColor: errors.fecha_emision ? "#ef4444" : undefined }}
                  value={form.fecha_emision}
                  onChange={(e) => handleFechaEmision(e.target.value)} />
                {errors.fecha_emision && <p style={{ color: "#f87171", fontSize: 12, margin: "3px 0 0" }}>{errors.fecha_emision}</p>}
              </div>
              <div className="col">
                <label>{t("masiva.fechaVencimiento")} <span style={{ color: "#f87171" }}>*</span></label>
                <input type="date" className="form-control"
                  style={{ colorScheme: "light dark", borderColor: errors.fecha_vencimiento ? "#ef4444" : undefined }}
                  value={form.fecha_vencimiento}
                  onChange={(e) => set("fecha_vencimiento", e.target.value)} />
                {errors.fecha_vencimiento && <p style={{ color: "#f87171", fontSize: 12, margin: "3px 0 0" }}>{errors.fecha_vencimiento}</p>}
              </div>
            </div>

            {/* Cuenta de ingreso */}
            <div>
              <label>{t("masiva.cuentaIngreso")} <span style={{ color: "#f87171" }}>*</span></label>
              <select className="form-select"
                value={form.cuenta_ingreso ?? ""}
                onChange={handleCuentaIngreso}
                style={{ borderColor: errors.cuenta_ingreso ? "#ef4444" : undefined }}>
                <option value="">{t("masiva.seleccionarCuenta")}</option>
                {cuentasIngreso.map((c) => (
                  <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                ))}
              </select>
              {errors.cuenta_ingreso && <p style={{ color: "#f87171", fontSize: 12, margin: "3px 0 0" }}>{errors.cuenta_ingreso}</p>}
            </div>

            {/* Concepto */}
            <div>
              <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
                {t("masiva.concepto")}
                <span style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 400 }}>
                  {t("masiva.conceptoAuto")}
                </span>
                <span style={{ color: "#f87171" }}>*</span>
              </label>
              <input type="text" className="form-control"
                placeholder={t("masiva.conceptoPlaceholder")}
                value={form.concepto}
                onChange={(e) => set("concepto", e.target.value)}
                style={{ borderColor: errors.concepto ? "#ef4444" : undefined }} />
              {errors.concepto && <p style={{ color: "#f87171", fontSize: 12, margin: "3px 0 0" }}>{errors.concepto}</p>}
              <div style={{ fontSize: 12, color: "var(--text-secondary)", marginTop: 3 }}>
                {t("masiva.conceptoHint")}
              </div>
            </div>

            {/* Monto */}
            <div>
              <label>
                {t("masiva.montoPorFilial")} <span style={{ color: "#f87171" }}>*</span>
                {isUSD && (
                  <span style={{ marginLeft: 6, fontSize: "0.75rem", color: "var(--text-muted)" }}>(en USD)</span>
                )}
              </label>
              <input type="number" min="0.01" step="0.01" className="form-control"
                placeholder={isUSD ? "100" : "50000"}
                value={form.monto}
                onChange={(e) => set("monto", e.target.value)}
                style={{ borderColor: errors.monto ? "#ef4444" : undefined }} />
              {errors.monto && <p style={{ color: "#f87171", fontSize: 12, margin: "3px 0 0" }}>{errors.monto}</p>}
            </div>

            {/* Moneda + TC */}
            <div style={{
              padding: "10px 14px",
              background: "var(--bg-card-alt, var(--bg-card))",
              border: "1px solid var(--border-card)",
              borderRadius: 8,
            }}>
              <MonedaSelector
                moneda={form.moneda || "CRC"}
                onMonedaChange={(m) => {
                  set("moneda", m);
                  if (m === "USD" && tipoCambio) set("tipo_cambio", tipoCambio.tipo_venta || 1);
                  if (m === "CRC") set("tipo_cambio", 1);
                }}
                tipoCambio={form.tipo_cambio || 1}
                onTCChange={(v) => set("tipo_cambio", v)}
                loadingTC={loadingTC}
                tcFuente={tipoCambio?.fuente || "BCCR"}
                monto={Number(form.monto) || 0}
              />
            </div>
          </div>
        </div>

        {/* ── Columna derecha: Filiales ───────────────────────────── */}
        <div className={styles["filial-details"]}>
          <div className={styles["filial-details-header"]}>
            <h1>
              <span className={styles["icon-filial-details"]}>
                <i className="fa-solid fa-building" />
              </span>
              {t("masiva.filialesLabel")}
              <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 400, color: "var(--text-secondary)" }}>
                {filialIds.size} / {filiales.length}
              </span>
            </h1>
          </div>

          {/* Buscador */}
          <div style={{
            display: "flex", alignItems: "center", gap: 7,
            background: "var(--bg-input)", borderRadius: 6,
            padding: "6px 10px", border: "1px solid var(--border-card)", margin: "12px 0 8px",
          }}>
            <i className="fa-solid fa-magnifying-glass" style={{ color: "var(--text-secondary)", fontSize: 12 }} />
            <input type="text" placeholder={t("masiva.buscarFilial")}
              value={search} onChange={(e) => setSearch(e.target.value)}
              style={{ flex: 1, background: "none", border: "none",
                color: "var(--text-primary)", fontSize: 14, outline: "none" }} />
          </div>

          {/* Seleccionar todas */}
          <button type="button" onClick={toggleTodas}
            style={{
              width: "100%", padding: "6px", background: "var(--bg-input)",
              border: "1px solid var(--border-card)", borderRadius: 5,
              color: "var(--text-secondary)", cursor: "pointer", fontSize: 13, transition: "color 0.2s",
            }}
            onMouseOver={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
            onMouseOut={(e)  => (e.currentTarget.style.color = "var(--text-secondary)")}
          >
            {filialesFiltradas.length > 0 && filialesFiltradas.every((f) => filialIds.has(f.id))
              ? t("masiva.deseleccionarTodas")
              : t("masiva.seleccionarTodas")}
          </button>

          {errors.filiales && (
            <p style={{ color: "#f87171", fontSize: 12, margin: "6px 0 0" }}>
              {errors.filiales}
            </p>
          )}

          {/* Lista */}
          <div style={{
            maxHeight: 380, overflowY: "auto", marginTop: 8,
            border: "1px solid var(--border-card)", borderRadius: 6,
            scrollbarWidth: "thin", scrollbarColor: "var(--border-card) transparent",
          }}>
            {filiales.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "var(--text-secondary)", fontSize: 13 }}>
                <i className="fa-solid fa-circle-notch fa-spin"
                  style={{ color: "#f97316", display: "block", fontSize: 18, marginBottom: 8 }} />
                {t("masiva.cargandoFiliales")}
              </div>
            ) : filialesFiltradas.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "var(--text-secondary)", fontSize: 13 }}>
                {t("masiva.sinCoincidencias")}
              </div>
            ) : filialesFiltradas.map((f, i) => (
              <label key={f.id} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 14px", cursor: "pointer",
                background: filialIds.has(f.id)
                  ? "rgba(249,115,22,0.1)"
                  : i % 2 === 0 ? "transparent" : "var(--hover-bg)",
                borderBottom: "1px solid var(--border-card)", transition: "background 0.1s",
              }}>
                <input type="checkbox"
                  checked={filialIds.has(f.id)}
                  onChange={() => toggleFilial(f.id)}
                  style={{ accentColor: "#f97316" }} />
                <span style={{ fontFamily: "Inter", color: "#f97316", fontSize: 12, minWidth: 46 }}>
                  {f.idFilial}
                </span>
                <span style={{ color: "var(--text-secondary)", fontSize: 13, flex: 1 }}>
                  {f.numPlano || "—"}
                </span>
              </label>
            ))}
          </div>

          {/* Total estimado */}
          {filialIds.size > 0 && Number(form.monto) > 0 && (
            <div style={{
              marginTop: 10, padding: "10px 12px",
              background: "var(--bg-card)", border: "1px solid var(--border-card)",
              borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "center",
            }}>
              <span style={{ color: "var(--text-secondary)", fontSize: 13 }}>
                {t("masiva.totalEstimado")}
              </span>
              <div style={{ textAlign: "right" }}>
                <span className="monto-typo" style={{ color: "#f97316", fontWeight: 700, fontSize: 14 }}>
                  {isUSD ? "$" : "₡"}{isUSD
                    ? (Number(form.monto) * filialIds.size).toFixed(2)
                    : fmt(Number(form.monto) * filialIds.size)}
                </span>
                {isUSD && (
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>
                    ≈ ₡{fmt(Number(form.monto) * filialIds.size * tc)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* ── Footer (span 2 columnas) ────────────────────────────── */}
        <div className={styles["invoice-footer"]}>
          <div className={styles.actions}>
            <Link to="/finanzas/facturas"
              className={`${styles.btn} ${styles["btn-clean-form"]}`}
              style={{ textDecoration: "none" }}>
              {t("masiva.cancelar")}
            </Link>
            <button onClick={handleGuardar} disabled={saving}
              className={`${styles.btn} ${styles["btn-save"]}`}
              style={{ opacity: saving ? 0.6 : 1 }}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" /> {t("masiva.creando")}</>
                : <><i className="fa-solid fa-bolt" /> {t("masiva.crearBtn", { n: filialIds.size })}</>
              }
            </button>
          </div>
        </div>

      </div>

      {emailModal && (
        <SendEmailModal
          facturas={emailModal}
          onClose={() => setEmailModal(null)}
          onSuccess={() => setEmailModal(null)}
        />
      )}
    </>
  );
}
